import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Lock, CheckCircle2, Circle, ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import { Overline, GoldRule } from "@/components/Bits";
import CheckoutDialog from "@/components/CheckoutDialog";
import { Progress } from "@/components/ui/progress";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/courses/${slug}`);
      setCourse(data);
    } catch {
      toast.error("Course not found");
      navigate("/academy");
    }
  }, [slug, navigate]);

  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    if (!user) return navigate("/login");
    setBusy(true);
    try {
      await api.post(`/enrollments/${slug}`);
      toast.success("You're enrolled! Start learning below.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleLesson = async (idx) => {
    try {
      const { data } = await api.post(`/enrollments/${slug}/progress`, { lesson_index: idx });
      setCourse((c) => ({ ...c, completed_lessons: data.completed_lessons }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!course) return <div className="min-h-screen" />;

  const completed = course.completed_lessons || [];
  const progress = course.lessons.length ? Math.round((completed.length / course.lessons.length) * 100) : 0;

  return (
    <div data-testid="course-detail-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-16 overflow-hidden border-b border-gold-500/15">
        <div className="absolute inset-0">
          <img src={course.image} alt={course.title} className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/70" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <Link to="/academy" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-500 hover:text-gold-300 transition-colors">
            <ArrowLeft size={14} /> All Courses
          </Link>
          <Overline className="mt-8 block">{course.category} · {course.level}</Overline>
          <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[0.92] text-white max-w-4xl">{course.title}</h1>
          <p className="mt-6 max-w-2xl text-lg text-white/75 leading-relaxed">{course.tagline}</p>
          <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-white/60">
            <span className="flex items-center gap-2"><Clock size={15} className="text-gold-500" /> {course.duration}</span>
            <span>{course.lessons.length} lessons</span>
            <span className="font-serif text-2xl text-gold-400">${course.price}</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 grid gap-14 lg:grid-cols-[1.6fr_1fr]">
        {/* Curriculum */}
        <div>
          <Reveal>
            <Overline>Course Overview</Overline>
            <p className="mt-5 text-lg text-white/75 leading-relaxed">{course.summary}</p>
            <GoldRule className="my-10" />
            <h2 className="font-serif text-3xl text-white mb-8">Curriculum</h2>
          </Reveal>

          {course.enrolled && (
            <div className="mb-8" data-testid="course-progress">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gold-400">{progress}% complete</span>
                <span className="text-white/50">{completed.length}/{course.lessons.length} lessons</span>
              </div>
              <Progress value={progress} className="h-1.5 bg-ink-paper [&>div]:bg-gold-500" />
            </div>
          )}

          <div className="space-y-3">
            {course.lessons.map((l, i) => {
              const done = completed.includes(i);
              return (
                <Reveal key={i} delay={i * 0.04}>
                  <div
                    className={`border p-6 transition-colors duration-300 ${
                      course.enrolled ? "border-gold-500/20 bg-ink-paper/40 hover:border-gold-400/50" : "border-white/10 bg-ink-paper/20"
                    }`}
                    data-testid={`lesson-${i}`}
                  >
                    <div className="flex items-start gap-4">
                      <span className="font-serif text-2xl text-gold-500/70 leading-none w-8">{String(i + 1).padStart(2, "0")}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-4">
                          <h4 className="font-serif text-xl text-white">{l.title}</h4>
                          {course.enrolled ? (
                            <button onClick={() => toggleLesson(i)} data-testid={`lesson-toggle-${i}`} className="shrink-0 text-gold-400 hover:text-gold-300 transition-colors">
                              {done ? <CheckCircle2 size={22} /> : <Circle size={22} className="text-white/40" />}
                            </button>
                          ) : (
                            <Lock size={16} className="text-white/40 shrink-0" />
                          )}
                        </div>
                        {l.duration && <span className="mt-1 block text-xs text-white/45">{l.duration}</span>}
                        {course.enrolled && l.body && (
                          <p className="mt-4 text-sm text-white/65 leading-relaxed">{l.body}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Enroll panel */}
        <div>
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <div className="glass corner-frame p-8">
                {course.enrolled ? (
                  <>
                    <p className="font-serif text-3xl text-gold-300">You're enrolled</p>
                    <p className="mt-3 text-sm text-white/65">Work through each lesson and mark it complete to track your progress.</p>
                    <div className="mt-6"><Progress value={progress} className="h-1.5 bg-ink [&>div]:bg-gold-500" /></div>
                    <p className="mt-3 text-xs text-white/50">{progress}% complete</p>
                    <Link to="/dashboard" className="mt-6 block text-center bg-gold-500 py-3.5 text-sm font-semibold text-ink hover:bg-gold-400 transition-colors">
                      Go to My Academy
                    </Link>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-white/60">Full access</p>
                    <p className="mt-1 font-serif text-5xl text-white">${course.price}</p>
                    <p className="mt-4 text-sm text-white/60 leading-relaxed">
                      Enrol to unlock every lesson, track your progress, and grow at your own pace.
                    </p>
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={enroll}
                      disabled={busy}
                      data-testid="enroll-btn"
                      className="mt-7 w-full bg-gold-500 py-4 text-sm font-semibold text-ink hover:bg-gold-400 transition-colors disabled:opacity-60"
                    >
                      {user ? "Enrol & Start Learning — Free" : "Sign in to Enrol"}
                    </motion.button>
                    <CheckoutDialog item={{ type: "course", slug: course.slug, title: course.title, price: course.price }}>
                      <button
                        data-testid="purchase-course-btn"
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-gold-500/40 py-3.5 text-sm text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors disabled:opacity-60"
                      >
                        <ShoppingBag size={15} /> Support · Purchase ${course.price}
                      </button>
                    </CheckoutDialog>
                    {!user && (
                      <p className="mt-4 text-center text-xs text-white/50">
                        New here? <Link to="/register" className="text-gold-400 hover:underline">Create an account</Link>
                      </p>
                    )}
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
